import { useState, useEffect } from "react";
import DashboardLayout from "../../components/layout/DashboardLayout";
import { useAuth } from "../../context/AuthContext";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { toast } from "sonner";
import { Search, Store, Download, UserPlus, Globe, ExternalLink } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminMerchants() {
  const { getAuthHeaders } = useAuth();
  const [merchants, setMerchants] = useState([]);
  const [affiliates, setAffiliates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [attributeDialogOpen, setAttributeDialogOpen] = useState(false);
  const [selectedMerchant, setSelectedMerchant] = useState(null);
  const [attributeForm, setAttributeForm] = useState({ affiliate_id: "", notes: "" });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const headers = { ...getAuthHeaders(), "Content-Type": "application/json" };
      
      const [merchantsRes, affiliatesRes] = await Promise.all([
        fetch(`${API_URL}/api/admin/merchants`, { credentials: "include", headers }),
        fetch(`${API_URL}/api/admin/affiliates`, { credentials: "include", headers }),
      ]);

      if (merchantsRes.ok) setMerchants(await merchantsRes.json());
      if (affiliatesRes.ok) setAffiliates(await affiliatesRes.json());
    } catch (error) {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const handleAttributeMerchant = async (e) => {
    e.preventDefault();
    
    try {
      const response = await fetch(`${API_URL}/api/admin/merchants/attribute`, {
        method: "POST",
        credentials: "include",
        headers: {
          ...getAuthHeaders(),
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          merchant_id: selectedMerchant.id,
          affiliate_id: attributeForm.affiliate_id,
          notes: attributeForm.notes,
        }),
      });

      if (response.ok) {
        toast.success("Merchant attributed!");
        setAttributeDialogOpen(false);
        setSelectedMerchant(null);
        setAttributeForm({ affiliate_id: "", notes: "" });
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to attribute merchant");
      }
    } catch (error) {
      toast.error("Failed to attribute merchant");
    }
  };

  const handleExport = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/export/merchants.csv`, {
        credentials: "include",
        headers: getAuthHeaders(),
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = "merchants.csv";
        a.click();
        window.URL.revokeObjectURL(url);
        toast.success("Export downloaded!");
      }
    } catch (error) {
      toast.error("Failed to export data");
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
    }).format(value || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const statuses = [...new Set(merchants.map((m) => m.status).filter(Boolean))];

  const filteredMerchants = merchants.filter((m) => {
    const matchesSearch =
      m.name?.toLowerCase().includes(search.toLowerCase()) ||
      m.maaly_merchant_id?.toLowerCase().includes(search.toLowerCase()) ||
      m.website?.toLowerCase().includes(search.toLowerCase());
    const matchesStatus = statusFilter === "all" || m.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  if (loading) {
    return (
      <DashboardLayout title="Merchants" subtitle="All referred merchants">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Merchants" subtitle="All referred merchants">
      <div data-testid="admin-merchants" className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row gap-4 justify-between">
          <div className="flex gap-4 flex-1">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
              <Input
                placeholder="Search merchants..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                data-testid="merchant-search"
                className="pl-10 bg-card border-border text-foreground"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[150px] bg-card border-border text-foreground">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent className="bg-card border-border">
                <SelectItem value="all">All Status</SelectItem>
                {statuses.map((status) => (
                  <SelectItem key={status} value={status}>
                    {status}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Button variant="outline" onClick={handleExport} data-testid="export-merchants-btn">
            <Download className="w-4 h-4 mr-2" /> Export CSV
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-card border border-border rounded-xl p-4">
            <p className="text-sm text-muted-foreground">Total</p>
            <p className="text-2xl font-bold text-foreground">{merchants.length}</p>
          </div>
          <div className="bg-card border border-border rounded-xl p-4">
            <p className="text-sm text-muted-foreground">Active</p>
            <p className="text-2xl font-bold text-emerald-400">
              {merchants.filter((m) => m.status === "active").length}
            </p>
          </div>
          <div className="bg-card border border-border rounded-xl p-4">
            <p className="text-sm text-muted-foreground">Attributed</p>
            <p className="text-2xl font-bold text-blue-400">
              {merchants.filter((m) => m.affiliate_id).length}
            </p>
          </div>
          <div className="bg-card border border-border rounded-xl p-4">
            <p className="text-sm text-muted-foreground">Unattributed</p>
            <p className="text-2xl font-bold text-amber-400">
              {merchants.filter((m) => !m.affiliate_id).length}
            </p>
          </div>
        </div>

        {/* Table */}
        <div className="bg-card border border-border rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Merchant</th>
                  <th>Maaly ID</th>
                  <th>Status</th>
                  <th>Country</th>
                  <th>Affiliate</th>
                  <th>Volume</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredMerchants.length > 0 ? (
                  filteredMerchants.map((merchant) => (
                    <tr key={merchant.id}>
                      <td>
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg bg-muted flex items-center justify-center">
                            <Store className="w-5 h-5 text-muted-foreground" />
                          </div>
                          <div>
                            <p className="font-medium text-foreground">{merchant.name}</p>
                            {merchant.website && (
                              <a
                                href={merchant.website}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-xs text-blue-400 hover:text-blue-300 flex items-center gap-1"
                              >
                                {merchant.website.replace(/https?:\/\//, "")}
                                <ExternalLink className="w-3 h-3" />
                              </a>
                            )}
                          </div>
                        </div>
                      </td>
                      <td>
                        <span className="text-xs font-mono text-muted-foreground">
                          {merchant.maaly_merchant_id}
                        </span>
                      </td>
                      <td>
                        <span
                          className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${
                            merchant.status === "active"
                              ? "status-active"
                              : merchant.status === "signed_up"
                              ? "status-pending"
                              : "bg-slate-500/20 text-muted-foreground border border-slate-500/30"
                          }`}
                        >
                          {merchant.status}
                        </span>
                      </td>
                      <td>
                        {merchant.country ? (
                          <div className="flex items-center gap-2">
                            <Globe className="w-4 h-4 text-slate-500" />
                            <span className="text-muted-foreground">{merchant.country}</span>
                          </div>
                        ) : (
                          <span className="text-slate-500">-</span>
                        )}
                      </td>
                      <td>
                        {merchant.affiliate_name ? (
                          <span className="text-muted-foreground">{merchant.affiliate_name}</span>
                        ) : (
                          <span className="text-slate-500 italic">Unattributed</span>
                        )}
                      </td>
                      <td className="text-muted-foreground">{formatCurrency(merchant.total_volume)}</td>
                      <td>
                        {!merchant.affiliate_id && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setSelectedMerchant(merchant);
                              setAttributeDialogOpen(true);
                            }}
                            data-testid={`attribute-${merchant.id}`}
                          >
                            <UserPlus className="w-4 h-4 mr-1" /> Attribute
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={7} className="text-center text-slate-500 py-8">
                      {search || statusFilter !== "all"
                        ? "No merchants match your filters"
                        : "No merchants yet"}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Attribute Dialog */}
        <Dialog open={attributeDialogOpen} onOpenChange={setAttributeDialogOpen}>
          <DialogContent className="bg-card border-border">
            <DialogHeader>
              <DialogTitle className="text-foreground">Attribute Merchant</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleAttributeMerchant} className="space-y-4 mt-4">
              <div className="p-4 rounded-lg bg-background border border-border">
                <p className="text-sm text-muted-foreground">Merchant</p>
                <p className="text-foreground font-medium">{selectedMerchant?.name}</p>
              </div>
              
              <div className="space-y-2">
                <Label className="text-muted-foreground">Assign to Affiliate</Label>
                <Select
                  value={attributeForm.affiliate_id}
                  onValueChange={(value) => setAttributeForm({ ...attributeForm, affiliate_id: value })}
                >
                  <SelectTrigger className="bg-background border-border text-foreground">
                    <SelectValue placeholder="Select affiliate" />
                  </SelectTrigger>
                  <SelectContent className="bg-card border-border">
                    {affiliates.map((affiliate) => (
                      <SelectItem key={affiliate.id} value={affiliate.id}>
                        {affiliate.name} ({affiliate.public_code})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label className="text-muted-foreground">Notes (optional)</Label>
                <Input
                  value={attributeForm.notes}
                  onChange={(e) => setAttributeForm({ ...attributeForm, notes: e.target.value })}
                  placeholder="Reason for manual attribution"
                  className="bg-background border-border text-foreground"
                />
              </div>

              <div className="flex gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setAttributeDialogOpen(false)}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={!attributeForm.affiliate_id}
                  className="flex-1 glow-primary"
                >
                  Attribute Merchant
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
